import { DISPLAY_LAST_USED_LIMIT } from 'appConstants';
import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import type {
  IGroupedProduct,
  IGroupedProducts,
  IProduct,
  IProductsState,
  IVirtualPack,
  IVirtualPackState,
} from './types';

export const selectProducts = (state: IProductsState) => state.products;
export const selectSubgroups = (state: IProductsState) => state.subGroups.data;
export const selectVirtualPacks = (state: IVirtualPackState) => state.data;

export const selectIndexedProducts = createSelector(selectProducts, (products) =>
  products.reduce((indexed: { [x: string]: any }, product: IProduct) => {
    if (product.packagedItemReference) {
      indexed[product.packagedItemReference] = product;
    }
    return indexed;
  }, {}),
);

export const selectIndexedVirtualPacks = createSelector(selectVirtualPacks, (products) =>
  products.reduce((indexed: { [x: string]: any }, product: IVirtualPack) => {
    if (product.packagedItemReference) {
      indexed[product.packagedItemReference] = product;
    }
    return indexed;
  }, {}),
);

export const selectProduct: Selector<IProductsState> = createSelector(
  [
    selectIndexedProducts,
    (_: IProductsState, packagedItemReference: string) => packagedItemReference,
  ],
  (indexedProducts, packagedItemReference) => indexedProducts[packagedItemReference],
);

export const selectVirtualPack: Selector<IVirtualPackState> = createSelector(
  [
    selectIndexedVirtualPacks,
    (_: IVirtualPackState, packagedItemReference: string) => packagedItemReference,
  ],
  (indexedProducts, packagedItemReference) => indexedProducts[packagedItemReference],
);

export const selectGroupedProducts: Selector<IProductsState, IGroupedProducts> = createSelector(
  selectIndexedProducts,
  (indexedProducts) => {
    let recentProductsCount = 0;

    return Object.values(indexedProducts).reduce(
      (indexed: { [x: string]: any }, product: IProduct) => {
        const {
          itemCode,
          packagedItemDescription,
          packagedItemReference,
          subGroupDescription = '',
        } = product;

        const groupKey = `${itemCode || packagedItemReference}_${packagedItemDescription.toLowerCase().replace(/\s/g, '')}`;

        let groupedProduct: IGroupedProduct = {
          itemCode: itemCode || '',
          itemGroupRef: groupKey,
          isRecent: recentProductsCount >= DISPLAY_LAST_USED_LIMIT ? false : product.isRecent,
          itemDescription: packagedItemDescription,
          subGroups: subGroupDescription ? [subGroupDescription] : [],
          packages: [product],
        };

        const existingIndexedProduct: IGroupedProduct = indexed[groupKey];

        if (existingIndexedProduct) {
          groupedProduct = {
            ...existingIndexedProduct,
            isRecent: Boolean(existingIndexedProduct.isRecent || groupedProduct.isRecent),
            subGroups: existingIndexedProduct.subGroups.includes(subGroupDescription)
              ? existingIndexedProduct.subGroups
              : [...existingIndexedProduct.subGroups, subGroupDescription],
            packages: [...existingIndexedProduct.packages, product],
          };
        }

        if (
          (!existingIndexedProduct || !existingIndexedProduct.isRecent) &&
          groupedProduct.isRecent
        ) {
          recentProductsCount++;
        }

        indexed[groupKey] = groupedProduct;

        return indexed;
      },
      {},
    );
  },
);

export const selectGroupedProduct: Selector<IProductsState> = createSelector(
  [selectGroupedProducts, (_: IProductsState, itemGroupRef: string) => itemGroupRef],
  (indexedProducts, itemGroupRef) => indexedProducts[itemGroupRef],
);
